/**
 * Ito layout primitives — thin, token-driven helpers for composing product
 * layouts. Every helper reads its rhythm from the design tokens
 * (`--stack-gap-*`, `--inset-*`, `--control-h-*`) so density switching
 * (comfortable/compact) restyles layouts automatically.
 */

import type { CSSProperties, ElementType, ReactNode } from "react";
import { cn } from "../lib/cn";

/** Named gap presets mapped to the density-aware stack gap tokens. */
export type StackGap = "none" | "2xs" | "xs" | "sm" | "md" | "lg" | "xl";

const GAP_TOKEN: Record<Exclude<StackGap, "none">, string> = {
  "2xs": "calc(var(--stack-gap-sm) * 0.5)",
  xs: "var(--stack-gap-sm)",
  sm: "var(--stack-gap-sm)",
  md: "var(--stack-gap-md)",
  lg: "var(--stack-gap-lg)",
  xl: "calc(var(--stack-gap-lg) * 1.5)",
};

const gapStyle = (gap: StackGap): string =>
  gap === "none" ? "0" : GAP_TOKEN[gap];

/* -------------------------------------------------------------------------- */
/* Stack                                                                      */
/* -------------------------------------------------------------------------- */

export interface StackProps {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Layout direction. */
  direction?: "row" | "column";
  /** Gap between children, from the density-aware stack gap tokens. */
  gap?: StackGap;
  align?: CSSProperties["alignItems"];
  justify?: CSSProperties["justifyContent"];
  wrap?: boolean;
  /** Render as a semantic element (default `div`). */
  as?: ElementType;
}

/**
 * Flexbox stack with density-aware gap tokens.
 *
 * @example
 * <Stack gap="md" align="start">
 *   <Title />
 *   <Meta />
 * </Stack>
 */
export function Stack({
  children,
  className,
  style,
  direction = "column",
  gap = "md",
  align,
  justify,
  wrap,
  as: Tag = "div",
}: StackProps) {
  return (
    <Tag
      className={cn("flex min-w-0", className)}
      style={{
        flexDirection: direction,
        gap: gapStyle(gap),
        alignItems: align,
        justifyContent: justify,
        flexWrap: wrap ? "wrap" : undefined,
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}

/** Vertical stack (the common case). */
export function VStack(props: Omit<StackProps, "direction">) {
  return <Stack direction="column" {...props} />;
}

/** Horizontal stack. */
export function HStack(props: Omit<StackProps, "direction">) {
  return <Stack direction="row" {...props} />;
}

/* -------------------------------------------------------------------------- */
/* Grid                                                                       */
/* -------------------------------------------------------------------------- */

export interface GridProps {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Fixed column count (1–12). */
  cols?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  /** Auto-fit as many columns of at least `minItemWidth` as fit. */
  minItemWidth?: string;
  gap?: StackGap;
  /** Row gap override; defaults to `gap`. */
  rowGap?: StackGap;
  align?: CSSProperties["alignItems"];
  as?: ElementType;
}

/**
 * CSS grid helper. Either a fixed column count or auto-fit with a minimum
 * item width.
 *
 * @example
 * <Grid minItemWidth="280px" gap="md">{cards}</Grid>
 */
export function Grid({
  children,
  className,
  style,
  cols,
  minItemWidth,
  gap = "md",
  rowGap,
  align,
  as: Tag = "div",
}: GridProps) {
  const template = minItemWidth
    ? `repeat(auto-fit, minmax(min(100%, ${minItemWidth}), 1fr))`
    : cols
      ? `repeat(${cols}, minmax(0, 1fr))`
      : undefined;
  return (
    <Tag
      className={cn("grid min-w-0", className)}
      style={{
        gridTemplateColumns: template,
        gap: `${gapStyle(rowGap ?? gap)} ${gapStyle(gap)}`,
        alignItems: align,
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}

/* -------------------------------------------------------------------------- */
/* Container                                                                  */
/* -------------------------------------------------------------------------- */

export interface ContainerProps {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Max content width; `full` spans the viewport. */
  width?: "sm" | "md" | "lg" | "xl" | "full";
  /** Pad the horizontal gutters. */
  gutter?: boolean;
  as?: ElementType;
}

const CONTAINER_WIDTH = {
  sm: "48rem",
  md: "64rem",
  lg: "80rem",
  xl: "96rem",
  full: undefined,
} as const;

/** Centered page container with gutter padding. */
export function Container({
  children,
  className,
  style,
  width = "xl",
  gutter = true,
  as: Tag = "div",
}: ContainerProps) {
  return (
    <Tag
      className={cn("mx-auto w-full", className)}
      style={{
        maxWidth: CONTAINER_WIDTH[width],
        paddingLeft: gutter ? "var(--inset-md)" : undefined,
        paddingRight: gutter ? "var(--inset-md)" : undefined,
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}

/* -------------------------------------------------------------------------- */
/* Spacer / Divider                                                           */
/* -------------------------------------------------------------------------- */

/** Flexible whitespace inside a Stack. */
export function Spacer({ size }: { size?: StackGap }) {
  return (
    <div
      aria-hidden
      style={{
        flexBasis: size ? gapStyle(size) : undefined,
        flexGrow: size ? 0 : 1,
        minWidth: 0,
      }}
    />
  );
}

export interface DividerProps {
  className?: string;
  style?: CSSProperties;
  /** Hairline color token (defaults to the ito hairline). */
  color?: "hairline" | "border";
  /** Inset the divider from the edges. */
  inset?: number;
}

/** Full-bleed hairline rule. */
export function Divider({
  className,
  style,
  color = "hairline",
  inset = 0,
}: DividerProps) {
  return (
    <div
      role="separator"
      className={cn("w-full shrink-0", className)}
      style={{
        height: 1,
        backgroundColor: `var(--${color})`,
        marginLeft: inset || undefined,
        marginRight: inset || undefined,
        ...style,
      }}
    />
  );
}

/** Vertical hairline rule for HStacks. */
export function VDivider({ className }: { className?: string }) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      className={cn("self-stretch shrink-0", className)}
      style={{ width: 1, backgroundColor: "var(--hairline)" }}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Surface                                                                    */
/* -------------------------------------------------------------------------- */

export interface SurfaceProps {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Elevation preset. */
  level?: "flat" | "raised" | "overlay";
  /** Pad with the density-aware inset tokens. */
  padding?: StackGap;
  as?: ElementType;
}

const SURFACE_LEVEL = {
  flat: {
    background: "var(--background)",
    border: "1px solid var(--hairline)",
  },
  raised: { background: "var(--card)", border: "1px solid var(--border)" },
  overlay: {
    background: "var(--popover)",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-lg)",
  },
} as const;

/**
 * A token-driven surface — the layout counterpart of `Card` when you need a
 * bare, unopinionated panel.
 */
export function Surface({
  children,
  className,
  style,
  level = "raised",
  padding,
  as: Tag = "div",
}: SurfaceProps) {
  const levelStyle = SURFACE_LEVEL[level];
  return (
    <Tag
      className={cn("rounded-lg", className)}
      style={{
        ...levelStyle,
        padding: padding ? gapStyle(padding) : undefined,
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}

/* -------------------------------------------------------------------------- */
/* PageHeader                                                                 */
/* -------------------------------------------------------------------------- */

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned actions. */
  actions?: ReactNode;
  /** Meta line under the description (badges, timestamps). */
  meta?: ReactNode;
  className?: string;
}

/** Standard product page header: title, description, meta and actions. */
export function PageHeader({
  title,
  description,
  actions,
  meta,
  className,
}: PageHeaderProps) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-start justify-between gap-4",
        className,
      )}
    >
      <div className="min-w-0">
        <h1 className="font-display text-title leading-tight tracking-tight">
          {title}
        </h1>
        {description && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
        {meta && (
          <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div>
        )}
      </div>
      {actions && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* ScrollRegion                                                               */
/* -------------------------------------------------------------------------- */

export interface ScrollRegionProps {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Constrain height and scroll vertically. */
  maxHeight?: string;
  /** Always show the scrollbar track. */
  alwaysScroll?: boolean;
}

/** A scrollable region with the product scrollbar styling. */
export function ScrollRegion({
  children,
  className,
  style,
  maxHeight,
  alwaysScroll,
}: ScrollRegionProps) {
  return (
    <div
      className={cn("min-w-0 overflow-auto", className)}
      style={{
        maxHeight,
        scrollbarWidth: alwaysScroll ? "thin" : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
